import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus } from 'lucide-react';
import * as api from '@/api/supervision.api';
import type { PedidoDeCambio } from '@/api/supervision.api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { fecha } from '@/lib/formato-tienda';

/**
 * LA VENTANITA — reunión del 12-oct: «que las colaboradoras reporten errores y pidan cambios
 * directamente en la plataforma». Escribe con sus palabras; le llega a la administración, que
 * contesta aquí mismo. No manda nada a nadie por fuera.
 */

const ESTADO: Record<PedidoDeCambio['estado'], { etiqueta: string; variant: 'default' | 'info' | 'success' | 'danger' | 'warning' }> = {
  NUEVO: { etiqueta: 'Recibido', variant: 'warning' },
  EN_ANALISIS: { etiqueta: 'Revisándolo', variant: 'info' },
  ACEPTADO: { etiqueta: 'Se va a hacer', variant: 'info' },
  RECHAZADO: { etiqueta: 'No se hará', variant: 'danger' },
  HECHO: { etiqueta: 'Hecho', variant: 'success' },
};

export function Ventanita() {
  const [abierta, setAbierta] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="mac-capsula w-11 lateral:w-9"
        aria-label="Reportar un error o pedir un cambio"
        title="Reportar un error o pedir un cambio"
      >
        <MessageSquarePlus className="h-4 w-4" strokeWidth={1.75} />
      </button>
      {abierta && <VentanaDePedidos onCerrar={() => setAbierta(false)} />}
    </>
  );
}

function VentanaDePedidos({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const [tipo, setTipo] = useState<'problema' | 'cambio'>('problema');
  const [texto, setTexto] = useState('');
  const [urgencia, setUrgencia] = useState<PedidoDeCambio['urgencia']>('molesta');
  const lista = useQuery({ queryKey: ['pedidos-de-cambio'], queryFn: api.pedidosDeCambio });

  const enviar = useMutation({
    mutationFn: () => api.pedirCambio({ tipo, quePasa: texto.trim(), pantalla: pathname, urgencia }),
    onSuccess: (r) => {
      toast.success(`Recibido (${r.codigo}). La administración lo revisa y te contesta aquí.`);
      setTexto('');
      void qc.invalidateQueries({ queryKey: ['pedidos-de-cambio'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo enviar'),
  });
  const contestar = useMutation({
    mutationFn: (p: { id: string; estado: PedidoDeCambio['estado'] }) => api.contestarPedido(p.id, { estado: p.estado }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pedidos-de-cambio'] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  return (
    <Modal open onClose={onCerrar} title="Reportar un error o pedir un cambio" size="lg">
      <div className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <Select label="Es…" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
            <option value="problema">Algo que falla</option>
            <option value="cambio">Una idea o un cambio</option>
          </Select>
          <Select label="¿Cuánto te afecta?" value={urgencia} onChange={(e) => setUrgencia(e.target.value as typeof urgencia)}>
            <option value="frena">No me deja trabajar</option>
            <option value="molesta">Me molesta</option>
            <option value="mejora">Sería mejor así</option>
          </Select>
        </div>
        <Textarea
          label={tipo === 'problema' ? '¿Qué pasó? ¿Qué esperabas que pasara?' : '¿Qué quieres que cambie y para qué?'}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={4}
          placeholder={
            tipo === 'problema'
              ? 'Le di a «Pasar venta» y no salió el comprobante…'
              : 'Que en el botón de pausa haya una opción de «Reunión»…'
          }
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button onClick={() => enviar.mutate()} disabled={texto.trim().length < 3} loading={enviar.isPending}>
            Enviar
          </Button>
        </div>

        <div className="border-t border-surface-border pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            {lista.data?.puedeContestar ? 'Lo que ha pedido el equipo' : 'Lo que has pedido'}
          </p>
          {(lista.data?.items ?? []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Todavía nada.</p>
          ) : (
            <ul className="mt-1 max-h-72 divide-y divide-surface-border overflow-auto">
              {lista.data!.items.map((p) => (
                <li key={p.id} className="py-2 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-slate-400">{p.codigo}</span>{' '}
                      <span className="font-medium text-slate-900 dark:text-slate-100">{p.titulo}</span>
                    </span>
                    {lista.data!.puedeContestar ? (
                      <select
                        className="rounded-md border border-surface-border bg-white px-2 py-1 text-xs dark:bg-slate-900"
                        value={p.estado}
                        onChange={(e) => contestar.mutate({ id: p.id, estado: e.target.value as PedidoDeCambio['estado'] })}
                        aria-label={`Estado de ${p.codigo}`}
                      >
                        {Object.entries(ESTADO).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.etiqueta}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant={ESTADO[p.estado].variant}>{ESTADO[p.estado].etiqueta}</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {p.nombre} · {fecha(p.createdAt)}
                    {p.pantalla ? ` · en ${p.pantalla}` : ''}
                  </p>
                  {p.quePasa !== p.titulo && <p className="mt-1 whitespace-pre-line text-slate-600 dark:text-slate-300">{p.quePasa}</p>}
                  {p.respuesta && <p className="mt-1 rounded bg-slate-50 px-2 py-1 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{p.respuesta}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
